import React, { useState, useCallback, useRef } from 'react';
import {
  Image as ImageIcon, Check, X, Pencil, Loader2,
} from 'lucide-react';
import { imagesApi } from '../api/services';
import type { ImageInput, ImageAnalysisResult } from '../types/models';
import PriorityChip from '../components/PriorityChip';
import type { Priority } from '../types/models';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// ImagesPage — drop-zone upload + AI extraction preview.
// User drops an image, it's uploaded, then the AI extracts work items. The
// preview list is editable in-place before "Accept All" creates the work
// items in bulk; "Discard" tears down the upload.
// ---------------------------------------------------------------------------

export default function ImagesPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [currentImage, setCurrentImage] = useState<ImageInput | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [analysisResults, setAnalysisResults] = useState<ImageAnalysisResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const processFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPEG, PNG, GIF, WebP, or BMP)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be under 10 MB');
      return;
    }

    // Create instant local preview
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));

    setUploading(true);
    setError(null);
    setSuccess(null);
    setAnalysisResults([]);

    try {
      const image = await imagesApi.upload(file);
      setCurrentImage(image);

      // Automatically analyse after upload
      setAnalysing(true);
      const results = await imagesApi.analyse(image.id);
      setAnalysisResults(results);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload/analyse image');
    } finally {
      setUploading(false);
      setAnalysing(false);
    }
  }, [previewUrl]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const clearPreview = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  const handleAccept = async () => {
    if (!currentImage || analysisResults.length === 0) return;
    try {
      const workItems = await imagesApi.accept(currentImage.id, analysisResults);
      setSuccess(`Created ${workItems.length} work item(s) from image`);
      setCurrentImage(null);
      setAnalysisResults([]);
      clearPreview();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDiscard = async () => {
    if (!currentImage) return;
    try {
      await imagesApi.discard(currentImage.id);
      setCurrentImage(null);
      setAnalysisResults([]);
      clearPreview();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const updateResult = (index: number, updates: Partial<ImageAnalysisResult>) => {
    setAnalysisResults(prev => prev.map((r, i) => i === index ? { ...r, ...updates } : r));
  };

  const removeResult = (index: number) => {
    setAnalysisResults(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Image Capture</h1>
        <p className="text-sm text-muted-foreground">
          Upload screenshots, photos of whiteboards, email captures, error messages, or meeting slides.
          AI will extract actionable work items automatically.
        </p>
      </div>

      {error && (
        <Alert variant="error" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-3">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-4 md:flex-row">
        {/* Drop zone */}
        <div className="min-w-0 flex-1">
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
            onChange={handleFileSelect}
          />
          <button
            type="button"
            onClick={() => !uploading && !analysing && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            disabled={uploading || analysing}
            className={cn(
              'flex min-h-[200px] w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed text-center transition-colors',
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:border-primary hover:bg-primary/5',
              (uploading || analysing) && 'cursor-wait',
            )}
          >
            {uploading || analysing ? (
              <>
                <Loader2 className="mb-3 h-12 w-12 animate-spin text-muted-foreground" />
                <p className="text-base font-semibold">{uploading ? 'Uploading...' : 'Analysing with AI...'}</p>
                <p className="text-sm text-muted-foreground">Extracting work items from your image</p>
              </>
            ) : (
              <>
                <ImageIcon className="mb-3 h-12 w-12 text-muted-foreground" />
                <p className="mb-1 text-base font-semibold">Drop an image or click to upload</p>
                <p className="text-sm text-muted-foreground">
                  Supported: JPEG, PNG, GIF, WebP, BMP (max 10 MB)
                </p>
              </>
            )}
          </button>
        </div>

        {/* Preview + uploaded meta */}
        {(currentImage || previewUrl) && !analysing && (
          <div className="w-full shrink-0 md:w-[320px]">
            <Card className="overflow-hidden">
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Uploaded preview"
                  className="max-h-[240px] w-full border-b border-border bg-black/[0.03] object-contain"
                />
              )}
              {currentImage && (
                <CardContent>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Uploaded</p>
                  <p className="text-sm font-medium">{currentImage.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {(currentImage.fileSizeBytes / 1024).toFixed(0)} KB · {currentImage.contentType}
                  </p>
                  <span
                    className={cn(
                      'mt-2 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                      currentImage.status === 'Analysed'
                        ? 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    {currentImage.status}
                  </span>
                </CardContent>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Extraction preview */}
      {analysisResults.length > 0 && (
        <div className="mt-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">
              Extracted Work Items ({analysisResults.length})
            </h2>
            <div className="flex gap-2">
              <Button
                onClick={handleAccept}
                className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <Check className="h-4 w-4" />
                Accept All
              </Button>
              <Button variant="outline" onClick={handleDiscard} className="gap-1.5">
                <X className="h-4 w-4" />
                Discard
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {analysisResults.map((result, index) => (
              <Card key={index} className="border-l-[3px] border-l-primary">
                <CardContent>
                  {editIndex === index ? (
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`r-title-${index}`}>Title</Label>
                        <Input
                          id={`r-title-${index}`}
                          value={result.title}
                          onChange={(e) => updateResult(index, { title: e.target.value })}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`r-desc-${index}`}>Description</Label>
                        <Textarea
                          id={`r-desc-${index}`}
                          rows={2}
                          value={result.description || ''}
                          onChange={(e) => updateResult(index, { description: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor={`r-type-${index}`}>Type</Label>
                          <Input
                            id={`r-type-${index}`}
                            value={result.workItemType}
                            onChange={(e) => updateResult(index, { workItemType: e.target.value })}
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor={`r-pri-${index}`}>Priority</Label>
                          <Input
                            id={`r-pri-${index}`}
                            value={result.priority}
                            onChange={(e) => updateResult(index, { priority: e.target.value })}
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor={`r-dur-${index}`}>Duration (min)</Label>
                          <Input
                            id={`r-dur-${index}`}
                            type="number"
                            value={result.suggestedDurationMinutes}
                            onChange={(e) => updateResult(index, { suggestedDurationMinutes: parseInt(e.target.value) || 30 })}
                          />
                        </div>
                      </div>
                      <Button onClick={() => setEditIndex(null)}>Done Editing</Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="break-words text-base font-semibold">{result.title}</p>
                          {result.description && (
                            <p className="mt-0.5 text-sm text-muted-foreground">{result.description}</p>
                          )}
                        </div>
                        <div className="flex shrink-0 gap-0.5">
                          <Button size="icon-sm" variant="ghost" onClick={() => setEditIndex(index)} aria-label="Edit">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon-sm" variant="ghost" onClick={() => removeResult(index)} aria-label="Remove">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                          {result.workItemType}
                        </span>
                        <PriorityChip priority={result.priority as Priority} />
                        <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                          {result.suggestedDurationMinutes}m
                        </span>
                        {result.category && (
                          <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                            {result.category}
                          </span>
                        )}
                        {result.suggestedStrategicDriver && (
                          <span className="inline-flex items-center rounded-full border border-primary/60 bg-transparent px-2 py-0.5 text-[11px] font-medium text-primary">
                            → {result.suggestedStrategicDriver}
                          </span>
                        )}
                        {result.flags.map(f => (
                          <span key={f} className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                            {f}
                          </span>
                        ))}
                      </div>
                      {result.imageDescription && (
                        <p className="mt-2 text-xs italic text-muted-foreground">
                          Source: {result.imageDescription}
                        </p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
